import { useState, useEffect, useMemo, useCallback } from 'react';

export const useTeamManagement = (allTeams, updateTeams, updateRealResults, setIsLoading, setSuccess, setError) => {
  // Team points for league standing calculation
  const [teamPoints, setTeamPoints] = useState({});
  
  // Manual team positions for handling ties
  const [teamPositions, setTeamPositions] = useState({});
  
  // State for pending changes (not yet saved)
  const [pendingChanges, setPendingChanges] = useState(false);
  const [savedTeamPositions, setSavedTeamPositions] = useState({});
  
  // Drag & drop state
  const [draggedTeam, setDraggedTeam] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [teamOrder, setTeamOrder] = useState([]);

  // Load team points from localStorage on mount
  useEffect(() => {
    const savedPoints = localStorage.getItem('championsLeagueTeamPoints');
    if (savedPoints) {
      setTeamPoints(JSON.parse(savedPoints));
    }
  }, []);

  // Load team points from database when teams data is available
  useEffect(() => {
    if (allTeams && allTeams.length > 0) {
      // Hent eksisterende point fra holdene i databasen
      const pointsFromDB = {};
      allTeams.forEach(team => {
        if (team.points !== undefined) {
          pointsFromDB[team.name] = team.points;
        }
      });
      
      // Kun opdater hvis der er point i databasen
      if (Object.keys(pointsFromDB).length > 0) {
        setTeamPoints(pointsFromDB);
      }
    }
  }, [allTeams]);

  // Calculate team position based on points or manual input
  const getTeamPosition = useCallback((team) => {
    // Use saved manual position if set, otherwise calculate from points
    if (savedTeamPositions[team]) {
      return savedTeamPositions[team];
    }
    
    // Only calculate from points if there are no saved positions
    if (Object.keys(savedTeamPositions).length === 0) {
      const sortedTeams = Object.entries(teamPoints)
        .sort(([,a], [,b]) => (b || 0) - (a || 0));
      return sortedTeams.findIndex(([teamName]) => teamName === team) + 1;
    }
    
    // If there are saved positions but not for this team, return original position
    return allTeams.findIndex(t => t.name === team) + 1;
  }, [savedTeamPositions, teamPoints, allTeams]);

  // Handle points change and update league standing
  const handlePointsChange = useCallback((team, points) => {
    const newPoints = parseInt(points) || 0;
    setTeamPoints(prev => ({
      ...prev,
      [team]: newPoints
    }));

    // Mark as pending changes when points are changed
    setPendingChanges(true);
  }, []);

  // Handle manual position change
  const handlePositionChange = useCallback((team, position) => {
    const newPosition = parseInt(position) || 0;
    
    // Validate position (1-36)
    if (newPosition && (newPosition < 1 || newPosition > 36)) {
      return; // Don't update if invalid
    }
    
    // Check for duplicate positions in pending changes
    if (newPosition) {
      const existingPositions = Object.values(teamPositions);
      if (existingPositions.includes(newPosition)) {
        // Clear the conflicting position
        const conflictingTeam = Object.keys(teamPositions).find(t => 
          t !== team && teamPositions[t] === newPosition
        );
        if (conflictingTeam) {
          setTeamPositions(prev => ({
            ...prev,
            [conflictingTeam]: 0
          }));
        }
      }
    }
    
    setTeamPositions(prev => ({
      ...prev,
      [team]: newPosition
    }));
    
    // Mark as pending changes
    setPendingChanges(true);
  }, [teamPositions]);

  // Clear all manual positions and points
  const clearManualPositions = useCallback(() => {
    setTeamPositions({});
    setSavedTeamPositions({});
    setTeamPoints({});
    setTeamOrder([]); // Clear team order when manually clearing all changes
    setPendingChanges(false);
    setSuccess('Alle ændringer og point ryddet!');
    setError('');
  }, [setSuccess, setError]);

  // Drag & drop handlers
  const handleDragStart = useCallback((e, team, index) => {
    setDraggedTeam({ team, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', team); // Use simpler data format
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    // Don't modify opacity directly - let CSS handle it
  }, []);

  const handleDragEnd = useCallback((e) => {
    // Don't modify opacity directly - let CSS handle it
    setDraggedTeam(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, targetIndex) => {
    e.preventDefault();
    
    if (!draggedTeam || draggedTeam.index === targetIndex) {
      setDragOverIndex(null);
      return;
    }

    // Get current teams array (what's actually displayed in UI)
    const currentTeams = teamOrder.length > 0 ? teamOrder : 
      (Object.keys(savedTeamPositions).length > 0 ? 
        [...allTeams].sort((a, b) => {
          const positionA = savedTeamPositions[a.name] || getTeamPosition(a.name);
          const positionB = savedTeamPositions[b.name] || getTeamPosition(b.name);
          return positionA - positionB;
        }) : allTeams);

    // Create new team order by moving the dragged team to the target position
    const newTeamOrder = [...currentTeams];
    const draggedTeamData = newTeamOrder[draggedTeam.index];
    
    // Remove dragged team from original position
    newTeamOrder.splice(draggedTeam.index, 1);
    
    // Insert at new position (adjust index if dragging down)
    const insertIndex = draggedTeam.index < targetIndex ? targetIndex - 1 : targetIndex;
    newTeamOrder.splice(insertIndex, 0, draggedTeamData);
    
    // Update team order state
    setTeamOrder(newTeamOrder);
    
    // Update team positions based on new order
    const newPositions = {};
    newTeamOrder.forEach((team, index) => {
      newPositions[team.name || team] = index + 1;
    });
    
    // Update both teamPositions and savedTeamPositions to reflect the new order
    setTeamPositions(prev => ({
      ...prev,
      ...newPositions
    }));
    
    setSavedTeamPositions(prev => ({
      ...prev,
      ...newPositions
    }));
    
    setPendingChanges(true);
    setDragOverIndex(null);
  }, [draggedTeam, allTeams, teamOrder, savedTeamPositions, getTeamPosition]);

  // Save position changes
  const savePositionChanges = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Merge current changes with existing saved positions and points
      const mergedPositions = { ...savedTeamPositions };
      const mergedPoints = { ...teamPoints };
      
      // Add current manual positions to saved positions
      Object.entries(teamPositions).forEach(([team, position]) => {
        if (position) {
          mergedPositions[team] = position;
        }
      });
      
      // Add current points to saved points
      Object.entries(teamPoints).forEach(([team, pointsValue]) => {
        if (pointsValue !== undefined) {
          mergedPoints[team] = pointsValue;
        }
      });
      
      // Create final positions based on merged manual positions and points
      const finalPositions = { ...mergedPositions };
      
      // For teams without manual positions, calculate from points
      const teamsWithoutManualPosition = allTeams.filter(team => !finalPositions[team.name]);
      const sortedByPoints = teamsWithoutManualPosition.sort((a, b) => {
        const pointsA = mergedPoints[a.name] || 0;
        const pointsB = mergedPoints[b.name] || 0;
        return pointsB - pointsA;
      });
      
      // Assign positions to teams without manual positions
      let nextPosition = 1;
      sortedByPoints.forEach(team => {
        while (Object.values(finalPositions).includes(nextPosition)) {
          nextPosition++;
        }
        finalPositions[team.name] = nextPosition;
        nextPosition++;
      });
      
      // Update state with merged values
      setSavedTeamPositions(finalPositions);
      setTeamPoints(mergedPoints);
      
      // Clear current pending changes but preserve the team order from drag & drop
      setTeamPositions({});
      // Don't clear teamOrder - keep the drag & drop order
      
      // Update league standing - use teamOrder if available (from drag & drop), otherwise sort by positions
      const sortedTeams = teamOrder.length > 0 ? teamOrder : [...allTeams].sort((a, b) => {
        return finalPositions[a.name] - finalPositions[b.name];
      });

      // Opret TeamInput objekter med point og placeringer - brug sortedTeams for korrekt rækkefølge
      const teamInputs = sortedTeams.map(team => ({
        name: team.name || team, // Handle both Team objects and strings
        points: mergedPoints[team.name || team] || 0,
        position: finalPositions[team.name || team] || sortedTeams.indexOf(team) + 1
      }));

      // Gem holdenes point og placeringer til database
      await updateTeams({
        variables: {
          teams: teamInputs
        }
      });
      
      // Save to database - konverter Team objekter til strings (holdnavne)
      const leagueStandingNames = sortedTeams.map(team => team.name || team);

      const result = await updateRealResults({
        variables: {
          realResults: {
            leagueStanding: leagueStandingNames,
            playoffResults: [],
            roundOf16Results: [],
            quarterFinalResults: [],
            semiFinalResults: [],
            finalResult: {}
          }
        }
      });

      // Save to localStorage as backup
      localStorage.setItem('championsLeagueTeamPoints', JSON.stringify(mergedPoints));
      
      setPendingChanges(false);
      setSuccess('Hold point, placeringer og resultater gemt til database og lokalt!');
      setError('');
    } catch (error) {
      console.error('Error saving position changes:', error);
      setError(`Fejl ved gemning af placeringer: ${error.message}`);
      setSuccess('');
    } finally {
      setIsLoading(false);
    }
  }, [savedTeamPositions, teamPoints, teamPositions, allTeams, updateTeams, updateRealResults, setIsLoading, setSuccess, setError]);

  // Sorter hold baseret på point som standard, men kun opdater efter gem
  const teams = useMemo(() => {
    // Hvis der er en teamOrder (fra drag & drop), brug den
    if (teamOrder.length > 0) {
      return teamOrder;
    }
    
    // Hvis der er gemte placeringer, sorter baseret på dem
    if (Object.keys(savedTeamPositions).length > 0) {
      return [...allTeams].sort((a, b) => {
        const positionA = savedTeamPositions[a.name] || getTeamPosition(a.name);
        const positionB = savedTeamPositions[b.name] || getTeamPosition(b.name);
        return positionA - positionB;
      });
    }
    
    // Sorter efter point som standard (kun gemte point, ikke pending changes)
    const savedPoints = {};
    allTeams.forEach(team => {
      if (team.points !== undefined) {
        savedPoints[team.name] = team.points;
      }
    });
    
    // Hvis der er gemte point, sorter efter dem
    if (Object.keys(savedPoints).length > 0) {
      return [...allTeams].sort((a, b) => {
        const pointsA = savedPoints[a.name] || 0;
        const pointsB = savedPoints[b.name] || 0;
        
        // Hvis point er ens, brug placering som tiebreaker
        if (pointsA === pointsB) {
          return allTeams.indexOf(a) - allTeams.indexOf(b);
        }
        
        return pointsB - pointsA; // Højeste point først
      });
    }
    
    // Ellers vis holdene i deres originale rækkefølge
    return allTeams;
  }, [allTeams, savedTeamPositions, teamOrder, getTeamPosition]);

  return {
    // State
    teamPoints,
    teamPositions,
    pendingChanges,
    savedTeamPositions,
    draggedTeam,
    dragOverIndex,
    teamOrder,
    teams,
    
    // Handlers
    handlePointsChange,
    handlePositionChange,
    clearManualPositions,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    savePositionChanges,
    getTeamPosition
  };
};
